import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Sparkles, ArrowRight, Copy, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { INDIAN_STATES } from '../data/indianStates';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [schoolCode, setSchoolCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    school_name: '', admin_name: '', admin_email: '', password: '',
    phone: '', state: '', district: '', school_board: '', region: 'ap-south'
  });

  const districts = useMemo(() => {
    if (!formData.state) return [];
    const stateObj = INDIAN_STATES.find(s => s.name === formData.state);
    return stateObj ? stateObj.districts : [];
  }, [formData.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Call the register API directly to get school_code
      const response = await axios.post(`${API_URL}/auth/register`, formData);
      const { school_code, access_token, refresh_token } = response.data.data;
      setSchoolCode(school_code);

      // Store tokens and fetch user
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      toast.success('School registered successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(schoolCode);
    setCopied(true);
    toast.success('School code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleContinue = () => {
    // Force a page reload to pick up the stored tokens
    window.location.href = '/dashboard';
  };

  // Show the school code success screen
  if (schoolCode) {
    return (
      <div className="min-h-screen bg-[#0c1222] flex items-center justify-center p-4 relative overflow-hidden" data-testid="register-success-page">
        <div className="absolute top-[-200px] right-[20%] w-[500px] h-[500px] rounded-full bg-teal-500/[0.06] blur-[120px] pointer-events-none" />

        <div className="w-full max-w-md relative z-10 text-center" data-testid="school-code-display">
          <div className="mb-6">
            <div className="h-16 w-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="registration-success-title">School Registered!</h1>
            <p className="text-sm text-white/50">Your school has been created. Share this code with your staff for login.</p>
          </div>

          <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-8 backdrop-blur-xl mb-6">
            <p className="text-xs text-white/40 uppercase tracking-widest mb-3 font-medium">Your School Code</p>
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="text-4xl font-bold text-teal-400 tracking-[0.15em] font-mono" data-testid="generated-school-code">
                {schoolCode}
              </span>
              <button
                onClick={handleCopy}
                className="p-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] transition-colors"
                data-testid="copy-school-code-btn"
              >
                {copied ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                ) : (
                  <Copy className="h-5 w-5 text-white/40" />
                )}
              </button>
            </div>
            <p className="text-xs text-white/30">
              All teachers and staff will need this code to log in. Keep it safe!
            </p>
          </div>

          <Button
            onClick={handleContinue}
            className="w-full h-12 bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-xl"
            data-testid="continue-to-dashboard-btn"
          >
            Continue to Dashboard
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0c1222] flex items-center justify-center p-4 py-12 relative overflow-hidden" data-testid="register-page">
      <div className="absolute top-[-200px] right-[20%] w-[500px] h-[500px] rounded-full bg-teal-500/[0.06] blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-100px] left-[30%] w-[400px] h-[400px] rounded-full bg-emerald-500/[0.04] blur-[100px] pointer-events-none" />

      <div className="w-full max-w-2xl relative z-10" data-testid="register-card">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <div className="h-10 w-10 rounded-xl bg-teal-500 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-white tracking-tight">Ariskool</span>
          </Link>
          <h1 className="text-2xl font-bold text-white mb-1" data-testid="register-title">Register Your School</h1>
          <p className="text-sm text-white/40" data-testid="register-description">Start managing leaves for your school today — Free 7-day trial</p>
        </div>

        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 backdrop-blur-xl">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">School Name *</Label>
                <Input placeholder="e.g. Delhi Public School" value={formData.school_name} onChange={(e) => setFormData({ ...formData, school_name: e.target.value })} required className="h-11 bg-white/[0.05] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" data-testid="register-school-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">Admin Name *</Label>
                <Input placeholder="Full Name" value={formData.admin_name} onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })} required className="h-11 bg-white/[0.05] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" data-testid="register-admin-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">Admin Email *</Label>
                <Input type="email" placeholder="admin@school.edu.in" value={formData.admin_email} onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })} required className="h-11 bg-white/[0.05] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" data-testid="register-email-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">Phone</Label>
                <Input type="tel" placeholder="+91 98765 43210" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="h-11 bg-white/[0.05] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" data-testid="register-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">State / UT *</Label>
                <Select value={formData.state} onValueChange={(v) => setFormData({ ...formData, state: v, district: '' })}>
                  <SelectTrigger className="h-11 bg-white/[0.05] border-white/[0.1] text-white rounded-xl [&>span]:text-white/50" data-testid="register-state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 bg-[#1e293b] border-white/10 text-white">
                    {INDIAN_STATES.map((state) => (
                      <SelectItem key={state.name} value={state.name}>{state.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">District *</Label>
                <Select value={formData.district} onValueChange={(v) => setFormData({ ...formData, district: v })} disabled={!formData.state}>
                  <SelectTrigger className="h-11 bg-white/[0.05] border-white/[0.1] text-white rounded-xl [&>span]:text-white/50" data-testid="register-district-select">
                    <SelectValue placeholder={formData.state ? "Select district" : "Select state first"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 bg-[#1e293b] border-white/10 text-white">
                    {districts.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">Password *</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} placeholder="Min 8 characters" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength={8} className="h-11 bg-white/[0.05] border-white/[0.1] text-white rounded-xl placeholder:text-white/25 pr-10" data-testid="register-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">School Board</Label>
                <Select value={formData.school_board} onValueChange={(v) => setFormData({ ...formData, school_board: v })}>
                  <SelectTrigger className="h-11 bg-white/[0.05] border-white/[0.1] text-white rounded-xl [&>span]:text-white/50" data-testid="register-board-select">
                    <SelectValue placeholder="Select board" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e293b] border-white/10 text-white">
                    <SelectItem value="cbse">CBSE</SelectItem>
                    <SelectItem value="icse">ICSE / ISC</SelectItem>
                    <SelectItem value="state_board">State Board</SelectItem>
                    <SelectItem value="ib">IB (International)</SelectItem>
                    <SelectItem value="igcse">IGCSE / Cambridge</SelectItem>
                    <SelectItem value="nios">NIOS</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full h-12 bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-xl" disabled={loading} data-testid="register-submit-btn">
              {loading ? 'Creating Account...' : 'Create School Account'}
              {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-white/30">
          Already have an account?{' '}
          <Link to="/login" className="text-teal-400 hover:text-teal-300 font-medium" data-testid="register-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
